import type { CaseStudyImage, CaseStudySection } from './amexCaseStudy';
export const mckinseyIntro = {
  client: 'McKinsey',
  title: 'Designing the pitch that won a $10M data transformation',
  summary: 'Showing Aon what its own data could do, before the data was ready.',
  background: 'linear-gradient(180deg, #E9F3F5 0%, #B7CED3 100%)',
};

export const mckinseyMetrics = [
  { value: '$10M+', label: 'contract value' },
  { value: '4 months', label: 'project duration' },
  { value: '$12B+', label: 'revenue managed' },
];

export const mckinseySections: CaseStudySection[] = [
  {
    label: 'Problem',
    paragraphs: [
      'Aon, a major multinational insurer, had fragmented data and no complete view of its own clients. Fixing that would be a long, complex effort that needed funding. The bet was that showing what clean data pipelines could do for the business would unlock the money.',
    ],
  },
  {
    label: 'Challenge',
    paragraphs: [
      'The client was a large organization with many decision-makers and information scattered across teams. The views they wanted were built by hand in Excel and owned by different groups.',
    ],
  },
  {
    label: 'Key Decisions',
    bullets: [
      'Gave people something to react to. Even with incomplete data, visual artifacts helped the client see which data we needed.',
      'Focused on storytelling to convince non-technical executives of the value of data integration.',
      'Used the newly developed design system to show the client what their investment in design would look like.',
      'Grounded the work in research, interviewing many internal stakeholders and collecting samples of the reports and visualizations they already used, then prototyped the full product surface in Figma.',
    ],
  },
  {
    label: 'Results',
    paragraphs: [
      'The pitch won a $10M+ data transformation project and embedded McKinsey deeply in the client organization.',
    ],
  },
  {
    figures: [
      {
        src: 'https://cdn.magicpatterns.com/uploads/prRRS2whaA2FdLDEPMg3a8/01_Overview.jpg',
        alt: 'Client overview dashboard with a relationship map, profile, closed opportunities, revenue data, and current pipeline',
        caption: 'Client overview bringing relationships, revenue, and pipeline into one view.',
      },
      {
        src: 'https://cdn.magicpatterns.com/uploads/aeggSnrPoY7T2TAbDJHwmB/02_Profile.jpg',
        alt: 'Client profile with company overview, news, research reports, sales by segment, and market data',
        caption: 'Company profile with news, segment sales, and market data.',
      },
      {
        src: 'https://cdn.magicpatterns.com/uploads/fFsQp2fAXP4GUiaM9xNmrk/03_Colleagues.jpg',
        alt: 'Colleagues table listing roles, names, business lines, locations, and contact actions',
        caption: 'Every Aon colleague working with the client, in one list.',
      },
      {
        src: 'https://cdn.magicpatterns.com/uploads/v7zPaLEK8VMciBhCZa6NiS/04_Pipeline.jpg',
        alt: 'Client pipeline table grouped by lead, won, open, and lost status with estimated values',
        caption: 'Pipeline grouped by status, with estimated value for each deal.',
      },
      {
        src: 'https://cdn.magicpatterns.com/uploads/5ysxkUztFVCvRpdQcF4qYc/05_Metrics.jpg',
        alt: 'DAU metrics with an activity timeline, client planning, jeopardy management, proposals, and client feedback',
        caption: 'Timeline of client activity tied to revenue impact.',
      },
      {
        src: 'https://cdn.magicpatterns.com/uploads/diVFB6dasu2ocBGxYwc3LX/06_Revenue.jpg',
        alt: 'Revenue grid showing renewal and new revenue by region and solution line',
        caption: 'Revenue by region and solution line.',
      },
    ],
  },
];

export const mckinseyImages: CaseStudyImage[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/sfKm3XHCbfkUsCcP1EwqNJ/01_Overview.jpg',
    alt: 'Client overview dashboard with relationship map, profile, closed opportunities, revenue data, and pipeline',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/83BXQZX3svvbFZTfRwHN5D/02_Profile.jpg',
    alt: 'Client profile with company overview, news, sales by segment, and market data',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/ppfp7L3CL3jpUoTU9Bo7mY/03_Colleagues.jpg',
    alt: 'Colleagues table listing roles, business lines, and locations',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/3RWoStWWgy2bxe24EyaRCx/04_Pipeline.jpg',
    alt: 'Client pipeline grouped by lead, won, open, and lost status',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/ojBhJEmFZGY7kdhZkrDSWH/05_Metrics.jpg',
    alt: 'DAU metrics with an activity timeline, client planning, jeopardy management, and feedback',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/4rTiL7hhivNt9fFKQnbeYu/06_Revenue.jpg',
    alt: 'Revenue grid by region and solution line',
  },
];
